import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { patchIsEmpty } from '@klear/schema/patch'
import { isItemLine, paymentItemWireFromLine } from '@klear/payment/line-item'
import type { PaymentItemWire } from '@klear/payment/item.wire'
import toNumber from 'lodash/toNumber'
import { PAYABLE_PUBLISH_ACTION, type PayablePublishAction } from '@klear/payable/constants'
import type { Payable } from '@klear/payable/payable'
import type { PayableCreateWire, PayableUpdateWire } from '@klear/payable/payable.wire'
import { paymentInputFromPayable } from '@klear/payable/payment-input'
import type { PayablePaymentInput } from '@klear/payable/payable.wire'

export type PayableForm = {
  supplierId: number | null
  currency: CurrencyCode
  orderId: number | null
  purchaseOrderId: number | null
  operationalEventIds: number[]
  items: PaymentItemWire[]
  parentEventId: number | null
  payableId: number | null
  payment: PayablePaymentInput | null
  publishAction: PayablePublishAction
  effectiveDate: string | null
}

export type PayableFormDirty = {
  supplierId?: boolean
  currency?: boolean
  orderId?: boolean
  purchaseOrderId?: boolean
  operationalEventIds?: boolean
  items?: boolean
  parentEventId?: boolean
  payment?: boolean
  publishAction?: boolean
  effectiveDate?: boolean
}

export const EMPTY_PAYABLE_FORM: PayableForm = {
  supplierId: null,
  currency: DEFAULT_CURRENCY_CODE,
  orderId: null,
  purchaseOrderId: null,
  operationalEventIds: [],
  items: [],
  parentEventId: null,
  payableId: null,
  payment: null,
  publishAction: PAYABLE_PUBLISH_ACTION.PUBLISH,
  effectiveDate: null
}

function isDirty(dirty: PayableFormDirty, key: keyof PayableFormDirty): boolean {
  return dirty[key] === true
}

export function payableToForm(payable: Payable): PayableForm {
  return {
    supplierId: payable.supplierId != null ? toNumber(payable.supplierId) : null,
    currency: payable.amount.currency,
    orderId: payable.orderId ?? null,
    purchaseOrderId: payable.purchaseOrderId ?? null,
    operationalEventIds: payable.operationals.map((operational) => operational.id ?? 0).filter((id) => id > 0),
    items: payable.lineItems.filter(isItemLine).map(paymentItemWireFromLine),
    parentEventId: null,
    payableId: payable.id ?? null,
    payment: paymentInputFromPayable(payable),
    publishAction: PAYABLE_PUBLISH_ACTION.PUBLISH,
    effectiveDate: null
  }
}

export function payableFormToCreateWire(form: PayableForm): PayableCreateWire {
  if (form.operationalEventIds.length === 0 && form.items.length === 0) {
    throw new Error('Add an operational event or a line item before saving the payable')
  }
  if (form.payment == null) {
    throw new Error('payment is required')
  }

  const wire: PayableCreateWire = {
    operationalEventIds: [...form.operationalEventIds],
    supplierId: form.supplierId ?? undefined,
    items: form.items.length > 0 ? [...form.items] : undefined,
    payment: form.payment,
    publishAction: form.publishAction ?? PAYABLE_PUBLISH_ACTION.PUBLISH
  }

  if (form.effectiveDate != null && form.effectiveDate.length > 0) {
    wire.effectiveDate = form.effectiveDate
  }

  return wire
}

export function payableFormToUpdateWire(form: PayableForm, dirty: PayableFormDirty): PayableUpdateWire {
  const wire: PayableUpdateWire = {}

  if (isDirty(dirty, 'operationalEventIds')) {
    wire.operationalEventIds = [...form.operationalEventIds]
  }
  if (isDirty(dirty, 'items')) {
    wire.items = [...form.items]
  }
  if (isDirty(dirty, 'supplierId') && form.supplierId != null) {
    wire.supplierId = form.supplierId
  }
  if (isDirty(dirty, 'payment') && form.payment != null) {
    wire.payment = form.payment
  }
  if (isDirty(dirty, 'publishAction')) {
    wire.publishAction = form.publishAction ?? PAYABLE_PUBLISH_ACTION.PUBLISH
  }
  if (isDirty(dirty, 'effectiveDate')) {
    wire.effectiveDate = form.effectiveDate != null && form.effectiveDate.length > 0 ? form.effectiveDate : null
  }

  if (patchIsEmpty(wire)) {
    throw new Error('No dirty payable fields to update')
  }

  return wire
}
